import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import axiosInstance from "../../axios/axiosInstance";
import { toastApiError } from "../../helper/api/toastApiError";

const unwrapCollection = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.$values)) return value.$values;
  return [];
};

const rejectResponse = (data, thunkApi, fallback) => {
  const message = toastApiError(data, fallback);
  return thunkApi.rejectWithValue({ message });
};

const rejectError = (error, thunkApi, fallback) => {
  const message = toastApiError(error, fallback);
  return thunkApi.rejectWithValue({
    message,
    status: error?.response?.status ?? null,
  });
};

const refreshDriverAddresses = (thunkApi, targetOwnerUserId) =>
  thunkApi.dispatch(
    getDriverSavedAddresses({
      targetOwnerUserId,
      includeInherited: true,
      tag: null,
      searchText: null,
    })
  );

const showBatchResult = (result, operationLabel) => {
  const createdCount = Number(result?.createdCount) || 0;
  const duplicateCount = Number(result?.skippedDuplicateCount) || 0;
  const failedCount = Number(result?.failedCount) || 0;
  const items = unwrapCollection(result?.items);

  if (createdCount > 0) toast.success(`${createdCount} adres ${operationLabel}.`);
  if (duplicateCount > 0) toast.info(`${duplicateCount} adres zaten kayıtlıydı.`);

  const failedMessages = [
    ...new Set(
      items
        .filter((item) => item?.status === "failed" && item?.message)
        .map((item) => item.message)
    ),
  ];

  if (failedMessages.length > 0) {
    failedMessages.forEach((message) => toast.error(message));
  } else if (failedCount > 0) {
    toast.error(`${failedCount} adres işlenemedi.`);
  }
};

export const getDriverSavedAddresses = createAsyncThunk(
  "driverAddress/getDriverSavedAddresses",
  async (
    {
      targetOwnerUserId,
      includeInherited = true,
      tag = null,
      searchText = null,
    },
    thunkApi
  ) => {
    try {
      const { data } = await axiosInstance.post(
        "/DriverAddress/GetDriverSavedAddressesForPortal",
        { targetOwnerUserId, includeInherited, tag, searchText }
      );

      if (data?.statusCode !== 200) {
        return rejectResponse(data, thunkApi, "Sürücünün adresleri alınamadı.");
      }

      return {
        targetOwnerUserId,
        items: unwrapCollection(data?.data),
      };
    } catch (error) {
      return rejectError(error, thunkApi, "Sürücünün adresleri alınamadı.");
    }
  }
);

export const addDriverSavedAddress = createAsyncThunk(
  "driverAddress/addDriverSavedAddress",
  async (payload, thunkApi) => {
    try {
      const { data } = await axiosInstance.post(
        "/DriverAddress/AddDriverSavedAddressFromPortal",
        payload
      );

      if (data?.statusCode !== 200) {
        return rejectResponse(data, thunkApi, "Adres sürücüye eklenemedi.");
      }

      showBatchResult(data?.data, "eklendi");
      refreshDriverAddresses(thunkApi, payload.targetOwnerUserIds[0]);
      return data?.data;
    } catch (error) {
      return rejectError(error, thunkApi, "Adres sürücüye eklenemedi.");
    }
  }
);

export const importCompanyAddressesToDriver = createAsyncThunk(
  "driverAddress/importCompanyAddressesToDriver",
  async (payload, thunkApi) => {
    try {
      const { data } = await axiosInstance.post(
        "/DriverAddress/ImportCompanyAddressesToDrivers",
        payload
      );

      if (data?.statusCode !== 200) {
        return rejectResponse(data, thunkApi, "Adresler sürücüye aktarılamadı.");
      }

      showBatchResult(data?.data, "aktarıldı");
      refreshDriverAddresses(thunkApi, payload.targetOwnerUserIds[0]);
      return data?.data;
    } catch (error) {
      return rejectError(error, thunkApi, "Adresler sürücüye aktarılamadı.");
    }
  }
);

export const updateDriverSavedAddress = createAsyncThunk(
  "driverAddress/updateDriverSavedAddress",
  async ({ targetOwnerUserId, address }, thunkApi) => {
    try {
      const { data } = await axiosInstance.post(
        "/DriverAddress/UpdateDriverSavedAddressFromPortal",
        address
      );

      if (data?.statusCode !== 200) {
        return rejectResponse(data, thunkApi, "Adres güncellenemedi.");
      }

      toast.success("Adres başarıyla güncellendi.");
      refreshDriverAddresses(thunkApi, targetOwnerUserId);
      return data?.data;
    } catch (error) {
      return rejectError(error, thunkApi, "Adres güncellenemedi.");
    }
  }
);

export const deleteDriverSavedAddress = createAsyncThunk(
  "driverAddress/deleteDriverSavedAddress",
  async ({ targetOwnerUserId, id }, thunkApi) => {
    try {
      const { data } = await axiosInstance.post(
        "/DriverAddress/DeleteDriverSavedAddressFromPortal",
        { id }
      );

      if (data?.statusCode !== 200) {
        return rejectResponse(data, thunkApi, "Adres silinemedi.");
      }

      toast.success("Adres sürücüden geri çekildi.");
      refreshDriverAddresses(thunkApi, targetOwnerUserId);
      return data?.data ?? { id };
    } catch (error) {
      return rejectError(error, thunkApi, "Adres silinemedi.");
    }
  }
);

export const getCompanyAddressesForDriverImport = createAsyncThunk(
  "driverAddress/getCompanyAddressesForDriverImport",
  async ({ page = 1, reset = false }, thunkApi) => {
    try {
      const { data } = await axiosInstance.get("/Load/GetKayitliAdresler", {
        params: { sayi: page, Il: "", Ilce: "" },
      });

      if (data?.statusCode !== 200) {
        return rejectResponse(data, thunkApi, "Firma adresleri alınamadı.");
      }

      const items = unwrapCollection(data?.data);
      return {
        items,
        page,
        reset,
        hasMore: items.length === 50,
      };
    } catch (error) {
      return rejectError(error, thunkApi, "Firma adresleri alınamadı.");
    }
  }
);

const initialState = {
  currentDriverUserId: null,
  items: [],
  isLoading: false,
  error: null,
  isMutating: false,
  companyAddresses: [],
  companyAddressPage: 0,
  hasMoreCompanyAddresses: true,
  isLoadingCompanyAddresses: false,
};

const driverAddressSlice = createSlice({
  name: "driverAddress",
  initialState,
  reducers: {
    resetDriverAddresses: () => initialState,
    resetCompanyAddressImportList: (state) => {
      state.companyAddresses = [];
      state.companyAddressPage = 0;
      state.hasMoreCompanyAddresses = true;
      state.isLoadingCompanyAddresses = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDriverSavedAddresses.pending, (state, action) => {
        state.isLoading = true;
        state.error = null;
        const nextDriverUserId = action.meta.arg.targetOwnerUserId;
        if (state.currentDriverUserId !== nextDriverUserId) state.items = [];
        state.currentDriverUserId = nextDriverUserId;
      })
      .addCase(getDriverSavedAddresses.fulfilled, (state, action) => {
        state.isLoading = false;
        if (state.currentDriverUserId === action.payload.targetOwnerUserId) {
          state.items = action.payload.items;
        }
      })
      .addCase(getDriverSavedAddresses.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message ?? "Sürücünün adresleri alınamadı.";
      })
      .addCase(addDriverSavedAddress.pending, (state) => {
        state.isMutating = true;
      })
      .addCase(addDriverSavedAddress.fulfilled, (state) => {
        state.isMutating = false;
      })
      .addCase(addDriverSavedAddress.rejected, (state) => {
        state.isMutating = false;
      })
      .addCase(importCompanyAddressesToDriver.pending, (state) => {
        state.isMutating = true;
      })
      .addCase(importCompanyAddressesToDriver.fulfilled, (state) => {
        state.isMutating = false;
      })
      .addCase(importCompanyAddressesToDriver.rejected, (state) => {
        state.isMutating = false;
      })
      .addCase(updateDriverSavedAddress.pending, (state) => {
        state.isMutating = true;
      })
      .addCase(updateDriverSavedAddress.fulfilled, (state) => {
        state.isMutating = false;
      })
      .addCase(updateDriverSavedAddress.rejected, (state) => {
        state.isMutating = false;
      })
      .addCase(deleteDriverSavedAddress.pending, (state) => {
        state.isMutating = true;
      })
      .addCase(deleteDriverSavedAddress.fulfilled, (state, action) => {
        state.isMutating = false;
        const deletedId = action.payload?.id;
        if (deletedId != null) {
          state.items = state.items.filter((item) => item.id !== deletedId);
        }
      })
      .addCase(deleteDriverSavedAddress.rejected, (state) => {
        state.isMutating = false;
      })
      .addCase(getCompanyAddressesForDriverImport.pending, (state) => {
        state.isLoadingCompanyAddresses = true;
      })
      .addCase(getCompanyAddressesForDriverImport.fulfilled, (state, action) => {
        state.isLoadingCompanyAddresses = false;
        const currentItems = action.payload.reset ? [] : state.companyAddresses;
        const byId = new Map(currentItems.map((item) => [item.id, item]));
        action.payload.items.forEach((item) => byId.set(item.id, item));
        state.companyAddresses = Array.from(byId.values());
        state.companyAddressPage = action.payload.page;
        state.hasMoreCompanyAddresses = action.payload.hasMore;
      })
      .addCase(getCompanyAddressesForDriverImport.rejected, (state) => {
        state.isLoadingCompanyAddresses = false;
      });
  },
});

export const { resetCompanyAddressImportList, resetDriverAddresses } =
  driverAddressSlice.actions;

export default driverAddressSlice.reducer;

